import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Layers,
  FileText,
  Download,
  Copy,
  Check,
  Trash2,
  Loader2,
  ChevronRight,
} from "lucide-react";
import api from "../api/client";

export default function SocialMonitorBundles() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cluster editing
  const [activePiece, setActivePiece] = useState("pillar");
  const [activePieceIndex, setActivePieceIndex] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editMeta, setEditMeta] = useState("");

  useEffect(() => {
    api.get(`/social/bundles/${id}/`).then((res) => {
      setBundle(res.data);
      if (res.data.bundle_type === "merged") {
        setEditContent(res.data.merged_content || "");
        setEditMeta(res.data.merged_meta_description || "");
      } else if (res.data.bundle_type === "cluster") {
        setEditContent(res.data.pillar_content || "");
        setEditMeta(res.data.pillar_meta_description || "");
      }
      setLoading(false);
    });
  }, [id]);

  const handleSavePiece = async () => {
    setSaving(true);
    try {
      const payload = {
        piece_type: activePiece,
        content_markdown: editContent,
        meta_description: editMeta,
      };
      if (activePiece === "supporting") {
        payload.piece_index = activePieceIndex;
      }
      const res = await api.patch(`/social/bundles/${id}/piece/`, payload);
      setBundle(res.data);
    } catch (err) {
      alert("Error saving piece");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this bundle?")) return;
    await api.delete(`/social/bundles/${id}/`);
    navigate("/social-monitor");
  };

  const handleDownload = () => {
    window.open(`${api.defaults.baseURL}/social/bundles/${id}/download/`, "_blank");
  };

  const copyContent = async () => {
    const text = bundle.bundle_type === "merged"
      ? bundle.merged_content
      : activePiece === "pillar"
      ? bundle.pillar_content
      : (bundle.supporting_posts_json?.[activePieceIndex]?.content || "");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectPiece = (type, index = null) => {
    setActivePiece(type);
    setActivePieceIndex(index);
    if (type === "pillar") {
      setEditContent(bundle.pillar_content || "");
      setEditMeta(bundle.pillar_meta_description || "");
    } else if (type === "supporting" && index !== null) {
      const sp = bundle.supporting_posts_json?.[index];
      setEditContent(sp?.content || "");
      setEditMeta(sp?.meta_description || "");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  if (!bundle) return <div className="text-center py-20 text-gray-500">Bundle not found</div>;

  const isMerged = bundle.bundle_type === "merged";
  const isCluster = bundle.bundle_type === "cluster";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/social-monitor")}
        className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Feed
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layers size={20} className="text-copper" />
          <div>
            <h1 className="text-lg font-bold text-white">
              {isMerged ? bundle.merged_title : bundle.pillar_title || `Bundle #${bundle.id}`}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                isMerged ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"
              }`}>
                {bundle.bundle_type_display}
              </span>
              <span className="text-[10px] text-gray-500">{bundle.source_mode_display}</span>
              <span className="text-[10px] text-gray-500">{bundle.business_display}</span>
              <span className="text-[10px] text-gray-600">{bundle.source_posts?.length || 0} source posts</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl flex items-center gap-2"
          >
            <Download size={14} />
            Download ZIP
          </button>
          <button
            onClick={handleDelete}
            className="px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-xl"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Merged blog editor */}
      {isMerged && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white">Merged Blog Content</h3>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={20}
            className="w-full px-4 py-3 bg-dark border border-dark-50 rounded-xl text-sm text-white font-mono focus:outline-none focus:border-copper/50 resize-y"
          />
          <div className="flex gap-2">
            <button
              onClick={copyContent}
              className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-xl text-sm flex items-center gap-2"
            >
              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy Markdown"}
            </button>
          </div>
          {/* HTML preview */}
          {bundle.merged_html && (
            <details className="mt-4">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-white">
                HTML Preview
              </summary>
              <div className="mt-2 border border-dark-50 rounded-xl overflow-hidden">
                <iframe
                  srcDoc={bundle.merged_html}
                  className="w-full h-96 bg-white"
                  title="Blog Preview"
                />
              </div>
            </details>
          )}
        </div>
      )}

      {/* Cluster editor */}
      {isCluster && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
            <h4 className="text-xs text-gray-500 uppercase font-semibold mb-2">Cluster Pieces</h4>
            <button
              onClick={() => selectPiece("pillar")}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                activePiece === "pillar"
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white hover:bg-dark-50/50"
              }`}
            >
              <FileText size={14} />
              Pillar Article
              {bundle.pillar_content && <Check size={12} className="ml-auto text-green-500" />}
            </button>
            {(bundle.supporting_posts_json || []).map((sp, i) => (
              <button
                key={i}
                onClick={() => selectPiece("supporting", i)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                  activePiece === "supporting" && activePieceIndex === i
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400 hover:text-white hover:bg-dark-50/50"
                }`}
              >
                <ChevronRight size={12} />
                <span className="truncate">{sp.title || `Supporting ${i + 1}`}</span>
                {sp.content && <Check size={12} className="ml-auto text-green-500" />}
              </button>
            ))}
          </div>

          {/* Editor */}
          <div className="lg:col-span-3 bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white">
              {activePiece === "pillar"
                ? bundle.pillar_title || "Pillar Article"
                : bundle.supporting_posts_json?.[activePieceIndex]?.title || `Supporting ${(activePieceIndex || 0) + 1}`}
            </h3>
            <input
              type="text"
              value={editMeta}
              onChange={(e) => setEditMeta(e.target.value)}
              placeholder="Meta description..."
              className="w-full px-4 py-2 bg-dark border border-dark-50 rounded-xl text-xs text-white placeholder-gray-600 focus:outline-none focus:border-copper/50"
            />
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={18}
              placeholder="Write content in markdown..."
              className="w-full px-4 py-3 bg-dark border border-dark-50 rounded-xl text-sm text-white font-mono placeholder-gray-600 focus:outline-none focus:border-copper/50 resize-y"
            />
            <div className="flex gap-2">
              {bundle.source_mode === "manual" && (
                <button
                  onClick={handleSavePiece}
                  disabled={saving}
                  className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Save
                </button>
              )}
              <button
                onClick={copyContent}
                className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-xl text-sm flex items-center gap-2"
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>

            {/* Preview */}
            {(activePiece === "pillar" ? bundle.pillar_html : bundle.supporting_posts_json?.[activePieceIndex]?.html) && (
              <details className="mt-4">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-white">
                  HTML Preview
                </summary>
                <div className="mt-2 border border-dark-50 rounded-xl overflow-hidden">
                  <iframe
                    srcDoc={activePiece === "pillar" ? bundle.pillar_html : bundle.supporting_posts_json?.[activePieceIndex]?.html}
                    className="w-full h-96 bg-white"
                    title="Preview"
                  />
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Source posts */}
      {bundle.source_posts?.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Source Posts</h3>
          <div className="space-y-2">
            {bundle.source_posts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-xs text-gray-400 py-1.5 border-b border-dark-50/50 last:border-0">
                <span className="font-mono text-gray-600">r/{p.subreddit}</span>
                <span className="text-white flex-1 truncate">{p.title}</span>
                <span className="text-copper font-bold">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
